import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  Percent, 
  AlertTriangle, 
  Tags, 
  FileText,
  Bell,
  Save,
  RotateCcw,
  Plus,
  X,
  Settings as SettingsIcon,
  Loader2
} from "lucide-react";

const Impostazioni = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [newMovementCategory, setNewMovementCategory] = useState("");
  const [newPaymentMethod, setNewPaymentMethod] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento impostazioni");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings`, settings, getAuthHeaders());
      setSettings(response.data);
      toast.success("Impostazioni salvate con successo");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    if (!window.confirm("Ripristinare tutte le impostazioni ai valori predefiniti?")) return;
    try {
      const response = await axios.post(`${API}/settings/reset`, {}, getAuthHeaders());
      setSettings(response.data);
      toast.success("Impostazioni ripristinate");
    } catch (error) {
      toast.error("Errore nel ripristino");
    }
  };

  const addCostCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const response = await axios.post(
        `${API}/settings/categories/cost?category=${encodeURIComponent(newCategory)}`,
        {},
        getAuthHeaders()
      );
      setSettings({ ...settings, cost_categories: response.data.cost_categories });
      setNewCategory("");
      toast.success("Categoria aggiunta");
    } catch (error) {
      toast.error("Errore nell'aggiunta");
    }
  };

  const removeCostCategory = async (category) => {
    try {
      const response = await axios.delete(
        `${API}/settings/categories/cost/${encodeURIComponent(category)}`,
        getAuthHeaders()
      );
      setSettings({ ...settings, cost_categories: response.data.cost_categories });
      toast.success("Categoria rimossa");
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const addMovementCategory = async () => {
    if (!newMovementCategory.trim()) return;
    try {
      const response = await axios.post(
        `${API}/settings/categories/movement?category=${encodeURIComponent(newMovementCategory)}`,
        {},
        getAuthHeaders()
      );
      setSettings({ ...settings, movement_categories: response.data.movement_categories });
      setNewMovementCategory("");
      toast.success("Categoria aggiunta");
    } catch (error) {
      toast.error("Errore nell'aggiunta");
    }
  };

  const removeMovementCategory = async (category) => {
    try {
      const response = await axios.delete(
        `${API}/settings/categories/movement/${encodeURIComponent(category)}`,
        getAuthHeaders()
      );
      setSettings({ ...settings, movement_categories: response.data.movement_categories });
      toast.success("Categoria rimossa");
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const addPaymentMethod = async () => {
    if (!newPaymentMethod.trim()) return;
    try {
      const response = await axios.post(
        `${API}/settings/payment-methods?method=${encodeURIComponent(newPaymentMethod)}`,
        {},
        getAuthHeaders()
      );
      setSettings({ ...settings, invoice_payment_methods: response.data.invoice_payment_methods });
      setNewPaymentMethod("");
      toast.success("Metodo aggiunto");
    } catch (error) {
      toast.error("Errore nell'aggiunta");
    }
  };

  const removePaymentMethod = async (method) => {
    try {
      const response = await axios.delete(
        `${API}/settings/payment-methods/${encodeURIComponent(method)}`,
        getAuthHeaders()
      );
      setSettings({ ...settings, invoice_payment_methods: response.data.invoice_payment_methods });
      toast.success("Metodo rimosso");
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const updateField = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="impostazioni-page">
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title">Impostazioni</h1>
          <p className="page-subtitle">Configura e personalizza l'applicazione</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={resetSettings}
            className="rounded-full border-[#D2D2D7]"
            data-testid="reset-settings-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Ripristina
          </Button>
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full px-6"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salva Modifiche
          </Button>
        </div>
      </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="bg-[#F5F5F7] p-1 rounded-xl flex-wrap h-auto gap-1">
          <TabsTrigger value="company" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            <Building2 className="w-4 h-4 mr-2" />
            Azienda
          </TabsTrigger>
          <TabsTrigger value="taxes" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            <Percent className="w-4 h-4 mr-2" />
            Aliquote
          </TabsTrigger>
          <TabsTrigger value="risk" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Rischio
          </TabsTrigger>
          <TabsTrigger value="categories" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            <Tags className="w-4 h-4 mr-2" />
            Categorie
          </TabsTrigger>
          <TabsTrigger value="invoices" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            <FileText className="w-4 h-4 mr-2" />
            Fatture
          </TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            <Bell className="w-4 h-4 mr-2" />
            Notifiche
          </TabsTrigger>
        </TabsList>

        {/* Company Tab */}
        <TabsContent value="company">
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Informazioni Azienda
              </CardTitle>
              <CardDescription>Dati anagrafici dell'azienda</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Nome Azienda</Label>
                  <Input
                    value={settings?.company_name || ""}
                    onChange={(e) => updateField("company_name", e.target.value)}
                    placeholder="La Mia Azienda S.r.l."
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Partita IVA</Label>
                  <Input
                    value={settings?.company_vat || ""}
                    onChange={(e) => updateField("company_vat", e.target.value)}
                    placeholder="IT12345678901"
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="company-vat"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Indirizzo</Label>
                <Input
                  value={settings?.company_address || ""}
                  onChange={(e) => updateField("company_address", e.target.value)}
                  placeholder="Via Roma 1, 00100 Roma (RM)"
                  className="h-11 rounded-xl border-[#D2D2D7]"
                  data-testid="company-address"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Telefono</Label>
                  <Input
                    value={settings?.company_phone || ""}
                    onChange={(e) => updateField("company_phone", e.target.value)}
                    placeholder="+39 06 12345678"
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="company-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Email</Label>
                  <Input
                    type="email"
                    value={settings?.company_email || ""}
                    onChange={(e) => updateField("company_email", e.target.value)}
                    placeholder="info@azienda.it"
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="company-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Valuta</Label>
                  <Input
                    value={settings?.currency || "EUR"}
                    onChange={(e) => updateField("currency", e.target.value)}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="currency"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Simbolo Valuta</Label>
                  <Input
                    value={settings?.currency_symbol || "€"}
                    onChange={(e) => updateField("currency_symbol", e.target.value)}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="currency-symbol"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Formato Data</Label>
                  <Input
                    value={settings?.date_format || "DD/MM/YYYY"}
                    onChange={(e) => updateField("date_format", e.target.value)}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="date-format"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Taxes Tab */}
        <TabsContent value="taxes">
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Aliquote Fiscali
              </CardTitle>
              <CardDescription>Configura le aliquote per calcoli fiscali</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">IVA Predefinita (%)</Label>
                  <Input
                    type="number"
                    value={settings?.iva_default || 22}
                    onChange={(e) => updateField("iva_default", parseFloat(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="iva-default"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">IRES (%)</Label>
                  <Input
                    type="number"
                    value={settings?.ires_rate || 24}
                    onChange={(e) => updateField("ires_rate", parseFloat(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="ires-rate"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">IRAP (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings?.irap_rate || 3.9}
                    onChange={(e) => updateField("irap_rate", parseFloat(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="irap-rate"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Aliquote IVA Disponibili</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {settings?.iva_rates?.map((rate) => (
                    <Badge key={rate} className="bg-[#007AFF]/10 text-[#007AFF] hover:bg-[#007AFF]/20 px-3 py-1">
                      {rate}%
                    </Badge>
                  ))}
                </div>
                <p className="text-sm text-[#86868B] mt-2">
                  Le aliquote IVA standard italiane sono: 0%, 4%, 10%, 22%
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risk Tab */}
        <TabsContent value="risk">
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Soglie di Rischio
              </CardTitle>
              <CardDescription>Configura i livelli di rischio per i clienti</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F] flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#34C759]" />
                    Soglia Rischio Basso
                  </Label>
                  <Input
                    type="number"
                    value={settings?.risk_threshold_low || 30}
                    onChange={(e) => updateField("risk_threshold_low", parseInt(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="risk-low"
                  />
                  <p className="text-xs text-[#86868B]">Score 0 - {settings?.risk_threshold_low || 30}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F] flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#FF9500]" />
                    Soglia Rischio Medio
                  </Label>
                  <Input
                    type="number"
                    value={settings?.risk_threshold_medium || 50}
                    onChange={(e) => updateField("risk_threshold_medium", parseInt(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="risk-medium"
                  />
                  <p className="text-xs text-[#86868B]">Score {settings?.risk_threshold_low || 30} - {settings?.risk_threshold_medium || 50}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F] flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#FF3B30]" />
                    Soglia Rischio Alto
                  </Label>
                  <Input
                    type="number"
                    value={settings?.risk_threshold_high || 70}
                    onChange={(e) => updateField("risk_threshold_high", parseInt(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="risk-high"
                  />
                  <p className="text-xs text-[#86868B]">Score &gt; {settings?.risk_threshold_high || 70}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-black/5">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Giorni Ritardo - Warning</Label>
                  <Input
                    type="number"
                    value={settings?.days_overdue_warning || 30}
                    onChange={(e) => updateField("days_overdue_warning", parseInt(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="days-warning"
                  />
                  <p className="text-xs text-[#86868B]">Giorni di ritardo per segnalazione gialla</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Giorni Ritardo - Critico</Label>
                  <Input
                    type="number"
                    value={settings?.days_overdue_critical || 60}
                    onChange={(e) => updateField("days_overdue_critical", parseInt(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="days-critical"
                  />
                  <p className="text-xs text-[#86868B]">Giorni di ritardo per segnalazione rossa</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cost Categories */}
            <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                  Categorie Costi
                </CardTitle>
                <CardDescription>Categorie per i costi funerali</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Nuova categoria..."
                    className="h-10 rounded-xl border-[#D2D2D7]"
                    onKeyPress={(e) => e.key === 'Enter' && addCostCategory()}
                    data-testid="new-cost-category"
                  />
                  <Button
                    onClick={addCostCategory}
                    size="icon"
                    className="h-10 w-10 bg-[#007AFF] hover:bg-[#0071E3] rounded-xl"
                    data-testid="add-cost-category-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settings?.cost_categories?.map((cat) => (
                    <Badge key={cat} className="bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED] px-3 py-1.5 flex items-center gap-2">
                      {cat}
                      <button 
                        onClick={() => removeCostCategory(cat)}
                        className="hover:text-[#FF3B30]"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Movement Categories */}
            <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                  Categorie Movimenti
                </CardTitle>
                <CardDescription>Categorie per i movimenti di cassa</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newMovementCategory}
                    onChange={(e) => setNewMovementCategory(e.target.value)}
                    placeholder="Nuova categoria..."
                    className="h-10 rounded-xl border-[#D2D2D7]"
                    onKeyPress={(e) => e.key === 'Enter' && addMovementCategory()}
                    data-testid="new-movement-category"
                  />
                  <Button
                    onClick={addMovementCategory}
                    size="icon"
                    className="h-10 w-10 bg-[#34C759] hover:bg-[#2DB14E] rounded-xl"
                    data-testid="add-movement-category-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settings?.movement_categories?.map((cat) => (
                    <Badge key={cat} className="bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED] px-3 py-1.5 flex items-center gap-2">
                      {cat}
                      <button 
                        onClick={() => removeMovementCategory(cat)}
                        className="hover:text-[#FF3B30]"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                  Metodi di Pagamento
                </CardTitle>
                <CardDescription>Metodi di pagamento per le fatture</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2 max-w-md">
                  <Input
                    value={newPaymentMethod}
                    onChange={(e) => setNewPaymentMethod(e.target.value)}
                    placeholder="Nuovo metodo..."
                    className="h-10 rounded-xl border-[#D2D2D7]"
                    onKeyPress={(e) => e.key === 'Enter' && addPaymentMethod()}
                    data-testid="new-payment-method"
                  />
                  <Button
                    onClick={addPaymentMethod}
                    size="icon"
                    className="h-10 w-10 bg-[#FF9500] hover:bg-[#E88B00] rounded-xl"
                    data-testid="add-payment-method-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settings?.invoice_payment_methods?.map((method) => (
                    <Badge key={method} className="bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED] px-3 py-1.5 flex items-center gap-2">
                      {method}
                      <button 
                        onClick={() => removePaymentMethod(method)}
                        className="hover:text-[#FF3B30]"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Impostazioni Fatture
              </CardTitle>
              <CardDescription>Configura numerazione e scadenze fatture</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Prefisso Fatture Attive</Label>
                  <Input
                    value={settings?.invoice_prefix_active || "FA"}
                    onChange={(e) => updateField("invoice_prefix_active", e.target.value)}
                    placeholder="FA"
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="invoice-prefix-active"
                  />
                  <p className="text-xs text-[#86868B]">Es: FA-001, FA-002...</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Prefisso Fatture Passive</Label>
                  <Input
                    value={settings?.invoice_prefix_passive || "FP"}
                    onChange={(e) => updateField("invoice_prefix_passive", e.target.value)}
                    placeholder="FP"
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="invoice-prefix-passive"
                  />
                  <p className="text-xs text-[#86868B]">Es: FP-001, FP-002...</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Giorni Scadenza Default</Label>
                  <Input
                    type="number"
                    value={settings?.invoice_default_payment_days || 30}
                    onChange={(e) => updateField("invoice_default_payment_days", parseInt(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="invoice-payment-days"
                  />
                  <p className="text-xs text-[#86868B]">Giorni dalla data emissione</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-black/5">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Mesi Trend Dashboard</Label>
                  <Input
                    type="number"
                    value={settings?.dashboard_months_trend || 6}
                    onChange={(e) => updateField("dashboard_months_trend", parseInt(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="dashboard-months"
                  />
                  <p className="text-xs text-[#86868B]">Numero di mesi nel grafico trend</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Giorni Cashflow</Label>
                  <Input
                    type="number"
                    value={settings?.cashflow_days || 60}
                    onChange={(e) => updateField("cashflow_days", parseInt(e.target.value))}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="cashflow-days"
                  />
                  <p className="text-xs text-[#86868B]">Giorni proiezione cashflow</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Notifiche e Avvisi
              </CardTitle>
              <CardDescription>Configura le notifiche automatiche</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-[#F5F5F7] rounded-xl">
                <div>
                  <p className="font-medium text-[#1D1D1F]">Notifica Clienti a Rischio</p>
                  <p className="text-sm text-[#86868B]">Ricevi avvisi quando un cliente supera la soglia di rischio</p>
                </div>
                <Switch
                  checked={settings?.notify_client_risk || false}
                  onCheckedChange={(checked) => updateField("notify_client_risk", checked)}
                  data-testid="notify-risk-switch"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Giorni Preavviso Scadenza Fatture</Label>
                <Input
                  type="number"
                  value={settings?.notify_invoice_expiring_days || 7}
                  onChange={(e) => updateField("notify_invoice_expiring_days", parseInt(e.target.value))}
                  className="h-11 rounded-xl border-[#D2D2D7] max-w-xs"
                  data-testid="notify-days"
                />
                <p className="text-xs text-[#86868B]">Giorni prima della scadenza per ricevere l'avviso</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Last Updated */}
      {settings?.updated_at && (
        <p className="text-sm text-[#86868B] text-center mt-8">
          Ultimo aggiornamento: {new Date(settings.updated_at).toLocaleString('it-IT')}
        </p>
      )}
    </div>
  );
};

export default Impostazioni;
